import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { UserService } from '../../core/user.service';
import { LinkFileListService } from '../../link-consume/services/link-file-list.service';
import { BlendEvent, User } from '../../shared/models';
import { BuildTransferItemService } from '../../transfer/services/build-transfer-item.service';
import { IFile } from 'src/sync';
import { FileItemService } from '../file-item.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BlendService } from '../../shared/services/blend.service';
import { canBrowserOpen, getFileExt } from '../../shared/func';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ApiService } from 'src/app/core/api.service';

@Component({
  selector: 'app-preview-default',
  templateUrl: './preview-default.component.html',
  styleUrls: ['./preview-default.component.scss']
})
export class PreviewDefaultComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public notice: number;
  @Input() public upsell: boolean;
  @Input() public item: IFile;
  public url: string;
  public openSpinner = false;

  //white label attributes
  public isWhiteLabel: boolean;
  public buttonTextColor: string;
  public buttonPrimaryColor: string;
  public linkTextColor: string;

  public allowComment: 0 | 1 | 2;

  private sub: Subscription;
  private breakpointSubscribe: Subscription;
  public isAuth: boolean;
  public user: User;
  public isCommentExpanded: boolean;
  public isUploadAllowed: number;
  public compat = 0;
  public isFileEditAllowed: number;
  public isCwdShared: number;
  public openFileLabel: string = '';
  public isSmallScreen: boolean = false;
  public type: 'filled' | 'outlined' | 'flat';
  leadIconAdornment: string = '';
  onClick: () => void = () => {};

  constructor(
    private buildTransferItemService: BuildTransferItemService,
    private linkPathListService: LinkFileListService,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private fileItemService: FileItemService,
    private router: Router,
    private route: ActivatedRoute,
    private blendService: BlendService,
    private breakpointObserver: BreakpointObserver,
    private apiService: ApiService,
  ) { }

  bypassSecurityTrustUrl(url) {
    if (url) {
      return this.sanitizer.bypassSecurityTrustUrl(url);
    }
    return null;
  }

  async ngOnInit() {
    this.isAuth = this.userService.isAuthenticated();
    this.user = this.userService.getUser();
    this.breakpointSubscribe = this.breakpointObserver
      .observe([`(max-width: 599px)`, `(min-width: 600px)`])
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.isSmallScreen = state.breakpoints['(max-width: 599px)']; // Update isSmallScreen
          this.updateOpenFileLabel();
        }
      });

    this.sub = this.linkPathListService
      .getSubscription()
      .subscribe((data) => {
        if (data.loaded && data.sorted) {
          this.isUploadAllowed = data.upload;
          this.compat = data.compat;
          this.isFileEditAllowed = data.file_edit;
          this.isCwdShared = data.cwd.is_shared;
          if (data.image_cachekey) {
            this.isWhiteLabel = true;
            this.buttonPrimaryColor = data.button_primary_color;
            this.buttonTextColor = data.button_text_color;
            this.linkTextColor = data.link_text_color;
          }
          this.allowComment = data.allow_comment;
        }
      });
    this.blendService.track(BlendEvent.VIEW_FILE, {
      type: 'default',
      preview_preference: 'N/A',
      fileSize: this.item.filesize,
      mimeType: this.item.mime_type
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.item &&
      changes.item.currentValue?.sync_id &&
      changes.item.previousValue?.sync_id &&
      changes.item.currentValue.sync_id !== changes.item.previousValue.sync_id
    ) {
      this.updateOpenFileLabel();
    }
  }

  updateOpenFileLabel(): void {
    if (this.isOfficeEdit()) {
      // If both conditions are true, set label for office edit
      this.openFileLabel = (!this.isSmallScreen ? 'Edit in ' : 'View in ') + this.fileItemService.getMSOfficeApp(this.item);
      this.type = 'outlined';
      this.leadIconAdornment = this.isSmallScreen ? 'preview' : 'edit';
      this.onClick = this.openFile;
      return;
    }

    if (this.isOpenable()) {
      // Handle special case for 'psd' files
      if (this.item.file_extension === 'psd') {
        this.openFileLabel = 'Download';
        this.type = this.user?.is_pro ? 'filled' : 'outlined';
        this.leadIconAdornment = 'download';
        this.onClick = this.downloadCompat;
        return;
      }

      // General case for other openable files
      const isVideoOrAudio = this.user?.is_pro && (this.fileItemService.isVideo(this.item) || this.fileItemService.isAudio(this.item));
      const shouldDownload = this.notice > 0 && (this.fileItemService.isOfficeDocument(this.item) || isVideoOrAudio);
      this.openFileLabel = shouldDownload ? 'Download' : 'Open in browser';
      this.leadIconAdornment = shouldDownload ? 'download' : 'open-in-new';
      this.onClick = shouldDownload ? this.downloadCompat : this.openFile;

      // Determine button type: 'filled' or 'outlined'
      if (this.notice > 0) {
        if (!this.user?.is_pro && this.fileItemService.isOfficeDocument(this.item)) {
          this.type = 'outlined';
        } else if (this.fileItemService.isPDF(this.item) || this.fileItemService.isText(this.item)) {
          this.type = 'outlined';
        } else if (!this.user?.is_pro && this.fileItemService.isVideo(this.item) || this.fileItemService.isAudio(this.item)) {
          this.type = 'outlined';
        } else {
          this.type = 'filled';
        }
      } else {
        // Check for 'Open in Browser' condition for non-pro users
        if (!this.user?.is_pro && this.openFileLabel === 'Open in browser') {
          this.type = 'outlined';
        } else {
          this.type = 'filled';
        }
      }
      return;
    }

    if (!this.item.previewonly) {
      // Handle case for non-previewable items
      this.openFileLabel = 'Download';
      this.type = 'filled';
      this.leadIconAdornment = 'download';
      this.onClick = this.downloadCompat;
      return;
    }
  }
  

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.breakpointSubscribe) {
      this.breakpointSubscribe.unsubscribe();
    }
  }

  public isOpenable() {
    if (this.item.context == 'link' && (this.item.previewonly == 8 || !this.compat)) {
      return false;
    }
    if (
      this.item.context == 'link' &&
      getFileExt(this.item.name) == 'pdf' &&
      !this.item.link_is_business
    ) {
      return false;
    }
    if (this.item.context == 'link' && this.fileItemService.isMSOffice(this.item, 'edit')) {
      return (
        this.fileItemService.isOfficeLinkEdit(this.item, this.isUploadAllowed, this.isFileEditAllowed, this.isCwdShared)
      );
    }
    const isVideo = this.fileItemService.isVideo(this.item);
    const isAudio = this.fileItemService.isAudio(this.item);
  
    if (this.fileItemService.isLikelyMediaFile(this.item.name) && !(isVideo || isAudio)) {
      return false;
    }
    if (!canBrowserOpen(this.item.name)) {
      return false;
    }
    // return this.item.compat
    //     ? canLinkBrowserOpen(this.item.name)
    //     : canBrowserOpen(this.item.name);
    return true;
  }

  public openFile = async () => {
    if (this.isOfficeEdit()) {
      this.openOfficeEdit();
    } else {
      if (this.item.context == 'applink') {
        this.url = this.item.compaturl;
      } else {
        this.url = await this.buildTransferItemService.getCompatUrl(
          this.item,
          this.buildTransferItemService.ACT_PREVIEW
        );
      }
      this.openInNewTab(this.url);
    }
  }


  public isOfficeEdit = () => {
    return (
      this.fileItemService.isMSOffice(this.item, 'edit') && this.isAuth
    );
  }

  public isOfficeDocument = () => {
    return this.fileItemService.isOfficeDocument(this.item);
  }

  public isPDF = () => {
    return this.fileItemService.isPDF(this.item);
  }

  openInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  public openOfficeEdit = () => {
    const isLinkEdit = this.item.context == 'link'
      ? this.fileItemService.isOfficeLinkEdit(this.item, this.isUploadAllowed, this.isFileEditAllowed, this.isCwdShared)
      : false;
    if (isLinkEdit) {
      const navigationParams = ['/4.0/dl', this.route.snapshot.params['cachekey'], 'view', 'officefse', this.item.sync_id];

      // add key in navigation params array if it exists in router params, otherwise key after # in fragment will be used
      if (this.route.snapshot.params['key']) { navigationParams.splice(2, 0, this.route.snapshot.params['key']); }
      this.router.navigate(navigationParams, {
        ...this.linkPathListService.decorateQueryParams(this.route.snapshot.queryParams),
        ...this.linkPathListService.decorateFragment(this.compat, this.route.snapshot.fragment)
      });
    } else if (this.item.context == 'files') {
      const syncId = this.item.sync_id;
      const viewParam = this.route.snapshot.queryParams['view'];
      const pageParam = this.route.snapshot.queryParams['page'];
      const viewSegment = this.isSmallScreen ? 'officefsv' : 'officefse';

      const url = this.router.createUrlTree(['/4.0/file', syncId, 'view', viewSegment], {
        queryParams: {
          view: viewParam,
          page: pageParam
        }
      }).toString();

      this.openInNewTab(url);
    }
  }

  public downloadCompat = () => {
    if (this.item.context == 'applink') {
      window.location.href = this.item.compaturl_dl;
    } else {
      this.linkConsume(this.item)
      this.buildTransferItemService.getCompatUrl(this.item, this.buildTransferItemService.ACT_DOWNLOAD).
        then((url) => {
          this.item.compaturl_dl = url;
          window.location.href = this.item.compaturl_dl;
        });
    }
  }
  public linkConsume(data: IFile) {
    if (data.linkID) {
      this.apiService.execute('linkconsume', {
        links: [{ publink_id: data.linkID }],
      });
    }
  }

  public onCommentToggle = (isCommentExpanded: boolean) => {
    this.isCommentExpanded = isCommentExpanded;
  }
}
